'use client';

import React from 'react';
import { useRoomContext, useParticipants } from '@livekit/components-react';
import { Hand } from 'lucide-react';
import { useHandRaise } from './HandRaiseContext';

export function HandRaiseBadge() {
  const room = useRoomContext();
  const participants = useParticipants();
  const { handRaisedParticipants, setHandRaised, getHandRaisedCount } = useHandRaise();

  const countHandRaised = React.useCallback(() => {
    let count = 0;
    const raisedSet = new Set<string>();

    participants.forEach((participant) => {
      try {
        // Tentar ler metadata primeiro
        const metadata = participant.metadata;
        if (metadata) {
          const parsed = JSON.parse(metadata);
          if (parsed.handRaised) {
            count++;
            raisedSet.add(participant.identity);
          }
        }
      } catch (e) {
        // Ignorar erros de parsing
      }
    });

    // Adicionar participantes que levantaram a mão via data channel
    handRaisedParticipants.forEach((identity) => {
      if (!raisedSet.has(identity)) {
        count++;
        raisedSet.add(identity);
      }
    });

    return count;
  }, [participants, handRaisedParticipants]);

  const handleDataReceived = React.useCallback(
    (payload: Uint8Array, participant: any) => {
      try {
        const data = JSON.parse(new TextDecoder().decode(payload));

        if (data.type === 'hand-raise') {
          setHandRaised(data.id, data.raised);
          
          if (data.raised && data.id !== room?.localParticipant?.identity) {
            try {
              const audio = new Audio('/sounds/new-notify.mp3');
              audio.volume = 0.4;
              audio.play().catch((e) => {
                console.warn("Audio play blocked", e);
              });
            } catch (error) {
              console.error("Erro ao tocar som de levantar mão", error);
            }
          }
        }
      } catch (e) {
        console.error('Erro ao processar data channel:', e);
      }
    },
    [setHandRaised, room?.localParticipant?.identity],
  );

  const handleParticipantConnected = React.useCallback(() => {
    // Quando novo participante conecta, recontar
    setTimeout(() => countHandRaised(), 100);
  }, [countHandRaised]);

  const handleParticipantDisconnected = React.useCallback(() => {
    // Quando participante desconecta, recontar
    setTimeout(() => countHandRaised(), 100);
  }, [countHandRaised]);

  React.useEffect(() => {
    if (!room) return;

    // Eventos de data channel - escutar tanto do room quanto do localParticipant
    room.on('dataReceived', handleDataReceived);

    // Também escutar do localParticipant para mensagens enviadas por ele mesmo
    if (room.localParticipant) {
      room.localParticipant.on('dataReceived', handleDataReceived);
    }

    // Eventos de participantes
    room.on('participantConnected', handleParticipantConnected);
    room.on('participantDisconnected', handleParticipantDisconnected);

    // Função para lidar com atualizações de participantes
    const handleParticipantUpdate = (participant: any) => {
      setTimeout(() => countHandRaised(), 100);
    };

    // Escutar mudanças de metadata de todos os participantes
    participants.forEach((participant) => {
      // Adicionar listener para mudanças de tracks (que podem indicar mudanças de metadata)
      participant.on('trackMuted', () => handleParticipantUpdate(participant));
      participant.on('trackUnmuted', () => handleParticipantUpdate(participant));
    });

    // Contar mais frequentemente para pegar mudanças de metadata
    const interval = setInterval(() => {
      countHandRaised();
    }, 1000);

    return () => {
      room.off('dataReceived', handleDataReceived);

      // Remover listener do localParticipant
      if (room.localParticipant) {
        room.localParticipant.off('dataReceived', handleDataReceived);
      }

      room.off('participantConnected', handleParticipantConnected);
      room.off('participantDisconnected', handleParticipantDisconnected);

      // Remover listeners de participantes
      participants.forEach((participant) => {
        participant.off('trackMuted', () => handleParticipantUpdate(participant));
        participant.off('trackUnmuted', () => handleParticipantUpdate(participant));
      });

      clearInterval(interval);
    };
  }, [
    room,
    participants,
    countHandRaised,
    handleDataReceived,
    handleParticipantConnected,
    handleParticipantDisconnected,
  ]);

  const handRaisedCount = getHandRaisedCount();

  if (handRaisedCount === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: '16px',
        left: '50%',
        transform: 'translateX(-50%)',
        background: '#ffd700', // Amarelo igual ao botão
        color: '#000000',
        padding: '8px 12px',
        borderRadius: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '14px',
        fontWeight: '600',
        zIndex: 1000,
        minWidth: '40px',
        justifyContent: 'center',
      }}
    >
      <Hand size={16} style={{ color: '#000000' }} />
      <span>{handRaisedCount}</span>
    </div>
  );
}
